import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  BrandSliceState,
  ChangeBrandVisibilityBodyType,
  ChangeSegmentBodyType,
  ChangeSettingsBodyType,
  IBrand,
  SettingsBrand,
} from "./types";
import $api from "@/shared/http";
import { toApiError } from "@/shared/http/types";

const initialState: BrandSliceState = {
  brandsList: [],
  settingsBrands: [],
  isLoading: false,
  hasNewBrands: false,
  dealerLoading: false,
  error: null,
};

//получение списка брендов с ограничениями для дилеров
export const fetchBrandsVisibility = createAsyncThunk<
  { items: IBrand[] },
  { dealerId: string },
  { rejectValue: string }
>("brand/fetchBrandsVisibility", async (body, { rejectWithValue }) => {
  try {
    const response = await $api.post<{ items: IBrand[] }>(
      "/brand/visibility",
      body,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const changeBrandsVisibility = createAsyncThunk<
  void,
  ChangeBrandVisibilityBodyType,
  { rejectValue: string }
>("brand/changeBrandsVisibility", async (body, { rejectWithValue }) => {
  try {
    await $api.put("/brand/visibility", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//получение списка брендов
export const fetchSettingsBrands = createAsyncThunk<
  { items: SettingsBrand[] },
  void,
  { rejectValue: string }
>("brand/fetchSettingsBrands", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<{ items: SettingsBrand[] }>(
      "/brand/category",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const changeSettingsBrands = createAsyncThunk<
  void,
  ChangeSettingsBodyType,
  { rejectValue: string }
>("brand/changeSettingsBrands", async (body, { rejectWithValue }) => {
  try {
    await $api.put("/brand/category", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const changeBrandSegment = createAsyncThunk<
  void,
  ChangeSegmentBodyType,
  { rejectValue: string }
>("brand/changeBrandSegment", async (body, { rejectWithValue }) => {
  try {
    await $api.put("/brand/marginality/segment", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const changeBrandClosingType = createAsyncThunk<
  void,
  {
    brandId: number;
    isClosedForAll: boolean;
    isOpenForAll?: boolean;
  },
  { rejectValue: string }
>("brand/changeBrandClosingType", async (body, { rejectWithValue }) => {
  try {
    await $api.post("/brand/category/closed", body);
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

//получение списка брендов
export const getNewBrands = createAsyncThunk<
  { items: IBrand[] },
  void,
  { rejectValue: string }
>("brand/getNewBrands", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.get<{ items: IBrand[] }>(
      "/brand/notification/new",
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message);
  }
});

export const brandsSlice = createSlice({
  name: "brands",
  initialState,
  reducers: {},

  extraReducers(builder) {
    builder.addCase(fetchBrandsVisibility.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchBrandsVisibility.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      state.brandsList = action.payload.items;
    });
    builder.addCase(fetchBrandsVisibility.rejected, (state, action) => {
      state.error = action.payload as string;
      state.isLoading = false;
    });
    builder.addCase(fetchSettingsBrands.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchSettingsBrands.fulfilled, (state, action) => {
      state.error = null;
      state.isLoading = false;
      state.settingsBrands = action.payload.items;
    });
    builder.addCase(fetchSettingsBrands.rejected, (state, action) => {
      state.error = action.payload as string;
      state.isLoading = false;
    });
    builder.addCase(changeSettingsBrands.pending, (state) => {
      state.error = null;
      state.dealerLoading = true;
    });
    builder.addCase(changeSettingsBrands.fulfilled, (state) => {
      state.error = null;
      state.dealerLoading = false;
    });
    builder.addCase(changeSettingsBrands.rejected, (state, action) => {
      state.error = action.payload as string;
      state.dealerLoading = false;
    });
    builder.addCase(changeBrandSegment.pending, (state) => {
      state.error = null;
      state.dealerLoading = true;
    });
    builder.addCase(changeBrandSegment.fulfilled, (state) => {
      state.error = null;
      state.dealerLoading = false;
    });
    builder.addCase(changeBrandSegment.rejected, (state, action) => {
      state.error = action.payload as string;
      state.dealerLoading = false;
    });
    builder.addCase(getNewBrands.pending, (state) => {
      state.error = null;
    });
    builder.addCase(
      getNewBrands.fulfilled,
      (state, action: PayloadAction<{ items: IBrand[] }>) => {
        state.error = null;
        state.hasNewBrands = !!action.payload?.items?.length;
      },
    );
    builder.addCase(getNewBrands.rejected, (state, action) => {
      state.error = action.payload as string;
    });
    builder.addCase(changeBrandsVisibility.pending, (state) => {
      state.error = null;
    });
    builder.addCase(changeBrandsVisibility.fulfilled, (state) => {
      state.error = null;
    });
    builder.addCase(changeBrandsVisibility.rejected, (state, action) => {
      state.error = action.payload as string;
    });
  },
});
